import { useState, useCallback } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";

interface Event {
	event_id: number;
	event_status: string;
	event_type: string;
	event_date: string;
	start_time: string;
	end_time: string;
}

interface Group {
	group_id: number;
	name: string;
	description: string;
	group_code: number;
	member_count?: number;
	max_members?: number;
	is_full?: boolean;
}

interface AffectedEvents {
	applied_events: Event[];
	booked_events: Event[];
	total_count: number;
}

interface JoinPreviewData {
	group: Group;
	affected_events: AffectedEvents;
}

interface UsePreviewJoinGroupReturn {
	previewJoinGroup: (groupCode: string) => Promise<void>;
	loading: boolean;
	error: string | null;
	previewData: JoinPreviewData | null;
	clearPreview: () => void;
}

/**
 * Custom hook to preview joining a group and see affected events
 */
export function usePreviewJoinGroup(): UsePreviewJoinGroupReturn {
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [previewData, setPreviewData] = useState<JoinPreviewData | null>(null);

	const previewJoinGroup = useCallback(async (groupCode: string) => {
		try {
			setLoading(true);
			setError(null);
			setPreviewData(null);

			const response: ApiResponse<JoinPreviewData> = await apiGet<JoinPreviewData>(
				`/groups/join-preview/${groupCode}`,
				undefined,
				{
					notification: {
						enabled: false,
						showError: false,
						showSuccess: false,
					},
				}
			);

			if (response.success && response.data) {
				setPreviewData(response.data);
			} else {
				setError(response.error || "Failed to preview group join");
			}
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else if (err instanceof Error) {
				setError(err.message);
			} else {
				setError("An unexpected error occurred");
			}
		} finally {
			setLoading(false);
		}
	}, []);

	const clearPreview = useCallback(() => {
		setPreviewData(null);
		setError(null);
	}, []);

	return {
		previewJoinGroup,
		loading,
		error,
		previewData,
		clearPreview,
	};
}